import React, {
  useContext,
  useState,
  useEffect,
  createContext,
  ReactNode,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { auth } from "./firebase-config";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  UserCredential,
  setPersistence,
  browserLocalPersistence,
  multiFactor,
} from "firebase/auth";
import Role, { OrganizationRole } from "../interfaces/identity/Role";
import Organization from "../interfaces/identity/Organization";
import UserInfo from "../interfaces/UserInfo";
import { OrganizationRolePolicyDto } from "../interfaces/superAdmin/SuperAdmin";
import { fetchAndStoreAvatars } from "../services/fileServices/FileService";
import {
  AbacPolicy,
  ResourceContext,
  doesUserHavePermission,
} from "../utilities/permissionUtils/PermissionUtils";
import { GetSubscriptionDetailsOfOrgRes } from "../interfaces";
import { GetSubscriptionDetailsOfOrgAsync } from "../services/PaymentServices/SubscriptionService";
import { UpdateSelectedUserForTaskSubject } from "../services/Subjects/userSubject";
import {
  shouldBlockRestoredUser,
  shouldClearImpersonationForAuthTransition,
  shouldInvalidateForActiveUserChange,
} from "./authTabPolicy";
import {
  measureAsync,
  performanceNow,
  recordPerformanceTiming,
} from "../utilities/performanceMetrics";
import {
  AuthenticationBootstrapData,
  AuthenticationBootstrapError,
  bootstrapAuthentication,
  mapBootstrapUserInfo,
} from "../services/userServices/AuthenticationBootstrapService";
import { clearTaskDetailMemoryCache } from "../services/taskServices/TaskDetailMemoryCache";

interface AuthContextType {
  currentUser: User | null;
  currentRoles: Role[] | null;
  currentOrganizationRoles: OrganizationRole[] | null;
  currentOrganization: Organization | null;
  currentUserInfo: UserInfo | null;
  currentOrganizationRolePolicies: OrganizationRolePolicyDto[] | null;
  userAddedInRolePolicy: boolean;
  currentPolicies: AbacPolicy[];
  subscriptionDetails: GetSubscriptionDetailsOfOrgRes | null;
  hasPermission: (action: string, ctx?: ResourceContext) => boolean;
  login: (email: string, password: string) => Promise<UserCredential>;
  register: (email: string, password: string) => Promise<UserCredential>;
  logout: () => Promise<void>;
  isLoading: boolean;
  authInitializationError: boolean;
  authInitializationFailureStatus: number | null;
  retryAuthInitialization: () => Promise<void>;
  completeInvitationAcceptance: (user: User) => Promise<void>;
  needsEmailVerification: boolean;
  needsMfaEnrollment: boolean;
  requiresPasswordChange: boolean | null;
  captureAuthIdentity: () => () => boolean;
  finalizePasswordChange: (isCurrentIdentity?: () => boolean) => Promise<boolean>;
  requiresOnboarding: boolean | null;
  setRequiresOnboarding: React.Dispatch<React.SetStateAction<boolean | null>>;
  impersonatedUserId: string | null;
  setImpersonation: (ghostId: string | null) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);
export default AuthContext;

interface AuthProviderProps {
  children: ReactNode;
}

const SESSION_TIMEOUT_MS = 43_200_000;
const SESSION_ACTIVITY_HEARTBEAT_MS = 60_000;
const SESSION_ACTIVITY_THROTTLE_MS = 30_000;
const AUTH_CHANNEL_NAME = "waivera_auth_channel";
const AUTH_TAB_ID_KEY = "waivera_auth_tab_id";
const AUTH_LAST_ACTIVITY_KEY = "waivera_auth_last_activity";
const AUTH_ACTIVE_USER_KEY = "waivera_auth_active_user_id";
const IMPERSONATED_USER_KEY = "impersonatedUserId";

const getAuthTabId = () => {
  let tabId = sessionStorage.getItem(AUTH_TAB_ID_KEY);

  if (!tabId) {
    tabId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    sessionStorage.setItem(AUTH_TAB_ID_KEY, tabId);
  }

  return tabId;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [currentRoles, setCurrentRoles] = useState<Role[] | null>(null);
  const [currentOrganizationRoles, setCurrentOrganizationRoles] = useState<
    OrganizationRole[] | null
  >(null);
  const [currentOrganization, setCurrentOrganization] =
    useState<Organization | null>(null);
  const [currentUserInfo, setCurrentUserInfo] = useState<UserInfo | null>(null);
  const [
    currentOrganizationRolePolicies,
    setCurrentOrganizationRolePolicies,
  ] = useState<OrganizationRolePolicyDto[]>([]);
  const [subscriptionDetails, setSubscriptionDetails] =
    useState<GetSubscriptionDetailsOfOrgRes | null>(null);
  const [userAddedInRolePolicy, setUserAddedInRolePolicy] =
    useState<boolean>(true);
  const [currentPolicies, setCurrentPolicies] = useState<AbacPolicy[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [authInitializationError, setAuthInitializationError] = useState(false);
  const [authInitializationFailureStatus, setAuthInitializationFailureStatus] = useState<number | null>(null);
  const [needsEmailVerification, setNeedsEmailVerification] = useState(false);
  const [needsMfaEnrollment, setNeedsMfaEnrollment] = useState(false);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState<
    boolean | null
  >(null);
  const [requiresOnboarding, setRequiresOnboarding] = useState<boolean | null>(
    null,
  );

  const [impersonatedUserId, setImpersonatedUserId] = useState<string | null>(
    () => localStorage.getItem(IMPERSONATED_USER_KEY),
  );

  const sessionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityWriteRef = useRef<number>(0);
  const loginInProgressRef = useRef(false);
  const authTabIdRef = useRef<string>(getAuthTabId());
  const listenerStartedAtRef = useRef<number | null>(null);
  const firstAuthCallbackRecordedRef = useRef(false);
  const identityTransitionPendingRef = useRef(false);
  const invitationBootstrapInProgressRef = useRef(false);
  const authInitializationGenerationRef = useRef(0);

  const clearSessionTimer = useCallback(() => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }
  }, []);

  const clearAuthState = useCallback(() => {
    authInitializationGenerationRef.current += 1;
    clearTaskDetailMemoryCache();
    clearSessionTimer();

    setCurrentUser(null);
    setCurrentRoles(null);
    setCurrentOrganizationRoles(null);
    setCurrentOrganization(null);
    setCurrentUserInfo(null);
    setCurrentPolicies([]);
    setCurrentOrganizationRolePolicies([]);
    setSubscriptionDetails(null);
    setUserAddedInRolePolicy(true);
    setNeedsEmailVerification(false);
    setNeedsMfaEnrollment(false);
    setRequiresPasswordChange(null);
    setRequiresOnboarding(null);
    setAuthInitializationError(false);
    setAuthInitializationFailureStatus(null);
    setImpersonatedUserId(null);

    UpdateSelectedUserForTaskSubject([]);
  }, [clearSessionTimer]);

  const broadcastExplicitLogout = useCallback(() => {
    const channel = new BroadcastChannel(AUTH_CHANNEL_NAME);

    channel.postMessage({
      type: "EXPLICIT_LOGOUT",
      tabId: authTabIdRef.current,
    });

    channel.close();
  }, []);

  const broadcastActiveUser = useCallback((userId: string) => {
    const channel = new BroadcastChannel(AUTH_CHANNEL_NAME);

    channel.postMessage({
      type: "ACTIVE_USER_CHANGED",
      tabId: authTabIdRef.current,
      userId,
    });

    channel.close();
  }, []);

  const setBrowserActiveUser = useCallback((
    userId: string,
    transition: "restore" | "explicit-login" = "explicit-login",
  ) => {
    if (shouldClearImpersonationForAuthTransition(transition)) {
      localStorage.removeItem("impersonatedUserId");
      setImpersonatedUserId(null);
    }
    localStorage.setItem(AUTH_ACTIVE_USER_KEY, userId);
    broadcastActiveUser(userId);
  }, [broadcastActiveUser]);

  const logout = useCallback(async (): Promise<void> => {
    clearSessionTimer();

    localStorage.removeItem("impersonatedUserId");
    localStorage.removeItem(AUTH_LAST_ACTIVITY_KEY);
    localStorage.removeItem(AUTH_ACTIVE_USER_KEY);
    sessionStorage.clear();

    clearAuthState();
    setIsLoading(false);

    broadcastExplicitLogout();

    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error during logout:", error);
    }
  }, [
    broadcastExplicitLogout,
    clearAuthState,
    clearSessionTimer,
  ]);

  const resetSessionTimer = useCallback((lastActivity?: number): void => {
    clearSessionTimer();

    const checkSessionTimeout = () => {
      const storedActivity = Number(localStorage.getItem(AUTH_LAST_ACTIVITY_KEY));
      const latestActivity = Number.isFinite(storedActivity) && storedActivity > 0
        ? storedActivity
        : lastActivity ?? Date.now();
      const remaining = SESSION_TIMEOUT_MS - (Date.now() - latestActivity);

      if (remaining > 0) {
        sessionTimeoutRef.current = setTimeout(checkSessionTimeout, remaining);
        return;
      }

      void logout();
    };

    const activityTimestamp = lastActivity ?? Number(localStorage.getItem(AUTH_LAST_ACTIVITY_KEY));
    const normalizedActivity = Number.isFinite(activityTimestamp) && activityTimestamp > 0
      ? activityTimestamp
      : Date.now();
    const delay = Math.max(0, SESSION_TIMEOUT_MS - (Date.now() - normalizedActivity));

    sessionTimeoutRef.current = setTimeout(checkSessionTimeout, delay);
  }, [clearSessionTimer, logout]);

  const recordSessionActivity = useCallback((force = false): void => {
    const now = Date.now();
    const storedActivity = Number(localStorage.getItem(AUTH_LAST_ACTIVITY_KEY));

    if (
      Number.isFinite(storedActivity) &&
      storedActivity > 0 &&
      now - storedActivity >= SESSION_TIMEOUT_MS
    ) {
      void logout();
      return;
    }

    if (!force && now - lastActivityWriteRef.current < SESSION_ACTIVITY_THROTTLE_MS) {
      return;
    }

    lastActivityWriteRef.current = now;
    localStorage.setItem(AUTH_LAST_ACTIVITY_KEY, String(now));
    resetSessionTimer(now);
  }, [logout, resetSessionTimer]);

  const applyBootstrapData = useCallback((bootstrap: AuthenticationBootstrapData) => {
    setRequiresPasswordChange(bootstrap.requiresPasswordChange);
    setRequiresOnboarding(bootstrap.requiresOnboarding);

    if (bootstrap.requiresOnboarding) {
      setCurrentRoles([]);
      setCurrentOrganizationRoles([]);
      setCurrentOrganization(null);
      setCurrentUserInfo(null);
      setCurrentPolicies([]);
      setCurrentOrganizationRolePolicies([]);
      setSubscriptionDetails(null);
      setUserAddedInRolePolicy(true);
      return;
    }

    setCurrentRoles(bootstrap.roles.employeeRoles || []);
    setCurrentOrganizationRoles(bootstrap.roles.organizationRoles || []);
    setCurrentOrganization(bootstrap.organization);
    setCurrentUserInfo(mapBootstrapUserInfo(bootstrap.userInfo));
    setCurrentPolicies(bootstrap.permissions || []);
    setCurrentOrganizationRolePolicies(bootstrap.rolePolicies || []);
    setUserAddedInRolePolicy(bootstrap.userAddedInRolePolicy);
    setSubscriptionDetails(bootstrap.subscription);
  }, []);

  const initializeAuthenticatedUser = useCallback(
    async (
      user: User,
      identityTransition = false,
      propagateFailure = false,
    ): Promise<boolean> => {
      const generation = ++authInitializationGenerationRef.current;
      const expectedImpersonatedUserId = localStorage.getItem(IMPERSONATED_USER_KEY);
      const isCurrentInitialization = () =>
        generation === authInitializationGenerationRef.current &&
        localStorage.getItem(IMPERSONATED_USER_KEY) === expectedImpersonatedUserId;
      const initializationStartedAt = performanceNow();
      let initializationSucceeded = false;
      setIsLoading(true);
      setAuthInitializationError(false);
      setAuthInitializationFailureStatus(null);
      setCurrentUser(user);

      try {
        const accessToken = await measureAsync("auth.id_token", () => user.getIdToken());
        if (!isCurrentInitialization()) return false;
        setNeedsEmailVerification(!user.emailVerified);

        const userMultiFactor = multiFactor(user);
        setNeedsMfaEnrollment(userMultiFactor.enrolledFactors.length === 0);

        resetSessionTimer();

        const bootstrap = await measureAsync("auth.bootstrap.aggregate", () =>
          bootstrapAuthentication(accessToken, identityTransition),
        );
        if (!isCurrentInitialization()) return false;
        applyBootstrapData(bootstrap);
        identityTransitionPendingRef.current = false;
        initializationSucceeded = true;

        // Billing and avatar data are presentation-only. Load them after the
        // protected shell is ready and ignore failures or obsolete identities.
        void measureAsync("auth.bootstrap.subscription_background", () =>
          GetSubscriptionDetailsOfOrgAsync(accessToken),
        )
          .then((subscriptionResponse) => {
            if (isCurrentInitialization()) {
              setSubscriptionDetails(subscriptionResponse?.data || null);
            }
          })
          .catch(() => {
            if (isCurrentInitialization()) {
              setSubscriptionDetails(null);
            }
          });

        void Promise.resolve().then(() => {
          if (isCurrentInitialization()) return fetchAndStoreAvatars(accessToken);
        });
      } catch (error) {
        if (!isCurrentInitialization()) return false;
        console.error("Authentication initialization failed.", {
          errorType: error instanceof Error ? error.name : "unknown",
        });

        setCurrentUser(user);
        setCurrentRoles([]);
        setCurrentOrganizationRoles([]);
        setCurrentOrganization(null);
        setCurrentUserInfo(null);
        setCurrentPolicies([]);
        setCurrentOrganizationRolePolicies([]);
        setSubscriptionDetails(null);
        setUserAddedInRolePolicy(true);
        setRequiresPasswordChange(null);
        setRequiresOnboarding(null);
        setAuthInitializationError(true);
        setAuthInitializationFailureStatus(
          error instanceof AuthenticationBootstrapError ? error.status : null,
        );
        identityTransitionPendingRef.current = identityTransition;
        if (propagateFailure) throw error;
      } finally {
        if (isCurrentInitialization()) {
          recordPerformanceTiming("auth.ready", initializationStartedAt, initializationSucceeded);
          setIsLoading(false);
        }
      }
      return initializationSucceeded && isCurrentInitialization();
    },
    [
      applyBootstrapData,
      resetSessionTimer,
    ],
  );

  const retryAuthInitialization = useCallback(async (): Promise<void> => {
    if (!currentUser) return;
    await initializeAuthenticatedUser(
      currentUser,
      identityTransitionPendingRef.current,
    );
  }, [currentUser, initializeAuthenticatedUser]);

  const completeInvitationAcceptance = useCallback(async (user: User): Promise<void> => {
    invitationBootstrapInProgressRef.current = true;
    try {
      clearTaskDetailMemoryCache();
      setBrowserActiveUser(user.uid);
      identityTransitionPendingRef.current = true;

      // Acceptance provisions the API identity after Firebase authentication has
      // already fired. Force a fresh token, then await the same guarded bootstrap
      // path used for other explicit identity transitions before routing onward.
      await user.getIdToken(true);
      const initialized = await initializeAuthenticatedUser(user, true, true);
      if (!initialized) {
        throw new Error("Invitation authentication was superseded before initialization completed.");
      }
    } finally {
      invitationBootstrapInProgressRef.current = false;
    }
  }, [initializeAuthenticatedUser, setBrowserActiveUser]);

  useEffect(() => {
    const channel = new BroadcastChannel(AUTH_CHANNEL_NAME);

    channel.onmessage = async (event) => {
      const message = event.data;

      if (!message || message.tabId === authTabIdRef.current) {
        return;
      }

      if (message.type === "ACTIVE_USER_CHANGED") {
        const nextUserId = typeof message.userId === "string" ? message.userId : "";

        if (!shouldInvalidateForActiveUserChange(currentUser?.uid, nextUserId)) return;

        clearSessionTimer();
        localStorage.removeItem("impersonatedUserId");
        clearAuthState();
        setIsLoading(false);
        return;
      }

      if (message.type !== "EXPLICIT_LOGOUT") return;

      clearSessionTimer();

      localStorage.removeItem("impersonatedUserId");
      localStorage.removeItem(AUTH_LAST_ACTIVITY_KEY);
      localStorage.removeItem(AUTH_ACTIVE_USER_KEY);
      sessionStorage.clear();

      clearAuthState();
      setIsLoading(false);

      try {
        await signOut(auth);
      } catch (error) {
        console.error("Error signing out after logout from another tab:", error);
      }
    };

    return () => {
      channel.close();
    };
  }, [clearAuthState, clearSessionTimer, currentUser?.uid]);

  useEffect(() => {
    if (!currentUser) return;

    const recordActivity = () => recordSessionActivity();
    const recordVisibleActivity = () => {
      if (document.visibilityState === "visible") {
        recordSessionActivity(true);
      }
    };
    const handleStorage = (event: StorageEvent) => {
      if (event.key === IMPERSONATED_USER_KEY) {
        clearTaskDetailMemoryCache();
        setImpersonatedUserId(event.newValue);
        identityTransitionPendingRef.current = true;
        void initializeAuthenticatedUser(currentUser, true);
        return;
      }

      if (event.key !== AUTH_LAST_ACTIVITY_KEY || !event.newValue) return;

      const activityTimestamp = Number(event.newValue);
      if (Number.isFinite(activityTimestamp) && activityTimestamp > 0) {
        resetSessionTimer(activityTimestamp);
      }
    };

    window.addEventListener("pointerdown", recordActivity, { passive: true });
    window.addEventListener("keydown", recordActivity);
    window.addEventListener("touchstart", recordActivity, { passive: true });
    window.addEventListener("focus", recordVisibleActivity);
    window.addEventListener("storage", handleStorage);
    document.addEventListener("visibilitychange", recordVisibleActivity);

    const visibleTabHeartbeat = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        recordSessionActivity(true);
      }
    }, SESSION_ACTIVITY_HEARTBEAT_MS);

    const storedActivity = Number(localStorage.getItem(AUTH_LAST_ACTIVITY_KEY));
    const hasExpiredStoredSession = Number.isFinite(storedActivity) &&
      storedActivity > 0 &&
      Date.now() - storedActivity >= SESSION_TIMEOUT_MS;

    if (hasExpiredStoredSession) {
      void logout();
    } else {
      recordSessionActivity(true);
    }

    return () => {
      window.removeEventListener("pointerdown", recordActivity);
      window.removeEventListener("keydown", recordActivity);
      window.removeEventListener("touchstart", recordActivity);
      window.removeEventListener("focus", recordVisibleActivity);
      window.removeEventListener("storage", handleStorage);
      document.removeEventListener("visibilitychange", recordVisibleActivity);
      window.clearInterval(visibleTabHeartbeat);
    };
  }, [currentUser, initializeAuthenticatedUser, logout, recordSessionActivity, resetSessionTimer]);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let isMounted = true;

    const initializeAuthListener = async () => {
      listenerStartedAtRef.current = performanceNow();
      setIsLoading(true);

      try {
        await setPersistence(auth, browserLocalPersistence);

        if (!isMounted) return;

        unsubscribe = onAuthStateChanged(auth, async (user) => {
          if (!isMounted) return;

          if (!firstAuthCallbackRecordedRef.current && listenerStartedAtRef.current != null) {
            firstAuthCallbackRecordedRef.current = true;
            recordPerformanceTiming("auth.listener.first_callback", listenerStartedAtRef.current);
          }

          if (!user) {
            clearAuthState();
            setIsLoading(false);
            return;
          }

          const activeUserId = localStorage.getItem(AUTH_ACTIVE_USER_KEY);
          if (shouldBlockRestoredUser(activeUserId, user.uid, loginInProgressRef.current)) {
            clearAuthState();
            setIsLoading(false);
            return;
          }

          if (!activeUserId) {
            // Restoring Firebase state is not a new login. Preserve an active
            // impersonation so API requests continue to receive its header.
            setBrowserActiveUser(user.uid, "restore");
          }

          // The invitation page explicitly owns this transition and awaits its
          // post-provision bootstrap. A late Firebase callback must not supersede
          // that authoritative initialization and let routing run ahead of it.
          if (invitationBootstrapInProgressRef.current) return;

          await initializeAuthenticatedUser(user);
        });
      } catch (error) {
        console.error("Error initializing Firebase auth persistence:", error);

        if (!isMounted) return;

        clearAuthState();
        setIsLoading(false);
      }
    };

    initializeAuthListener();

    return () => {
      isMounted = false;

      if (unsubscribe) {
        unsubscribe();
      }

      clearSessionTimer();
    };
  }, [clearAuthState, clearSessionTimer, initializeAuthenticatedUser, setBrowserActiveUser]);

  const setImpersonation = useCallback(
    async (ghostId: string | null): Promise<void> => {
      // Header callers hard-reload immediately after this promise resolves.
      // Clear a prior identity's focused request synchronously, before bootstrap
      // or React effects, so the next document cannot restore that stale filter.
      const previousTarget = localStorage.getItem(IMPERSONATED_USER_KEY) || null;
      if (previousTarget !== (ghostId || null)) {
        const location = new URL(window.location.href);
        if (location.pathname.replace(/\/+$/, "") === "/signatures" && location.searchParams.has("requestId")) {
          location.searchParams.delete("requestId");
          window.history.replaceState(window.history.state, "", `${location.pathname}${location.search}${location.hash}`);
        }
      }
      clearTaskDetailMemoryCache();
      if (ghostId) {
        localStorage.setItem(IMPERSONATED_USER_KEY, ghostId);
      } else {
        localStorage.removeItem(IMPERSONATED_USER_KEY);
      }

      setImpersonatedUserId(ghostId);

      if (!currentUser) return;
      await initializeAuthenticatedUser(currentUser, true);
    },
    [currentUser, initializeAuthenticatedUser],
  );

  // Snapshot the signed-in identity so long-running account flows can tell
  // whether this tab switched users or impersonation targets before acting.
  const captureAuthIdentity = useCallback((): (() => boolean) => {
    const generation = authInitializationGenerationRef.current;
    const impersonatedUserId = localStorage.getItem(IMPERSONATED_USER_KEY);
    const uid = auth.currentUser?.uid ?? null;
    return () =>
      uid !== null &&
      auth.currentUser?.uid === uid &&
      generation === authInitializationGenerationRef.current &&
      localStorage.getItem(IMPERSONATED_USER_KEY) === impersonatedUserId;
  }, []);

  // The change-password endpoint records the new password date. Reload the
  // account bootstrap with the restored session so the password-expiry gate
  // reflects the server's decision instead of being cleared locally.
  const finalizePasswordChange = useCallback(async (
    isCurrentIdentity: () => boolean = () => true,
  ): Promise<boolean> => {
    if (!currentUser || currentUser.uid !== auth.currentUser?.uid) return false;
    const requestIdentity = captureAuthIdentity();
    const isCurrent = () => isCurrentIdentity() && requestIdentity();
    if (!isCurrent()) return false;

    try {
      const idToken = await currentUser.getIdToken();
      const bootstrap = await bootstrapAuthentication(idToken);
      if (!isCurrent()) return false;

      applyBootstrapData(bootstrap);
      return bootstrap.requiresPasswordChange === false;
    } catch (error) {
      console.error("Error finalizing password change:", {
        errorType: error instanceof Error ? error.name : "unknown",
      });
      return false;
    }
  }, [applyBootstrapData, captureAuthIdentity, currentUser]);

  const login = useCallback(
    async (email: string, password: string): Promise<UserCredential> => {
      loginInProgressRef.current = true;

      try {
        const userCredential = await signInWithEmailAndPassword(
          auth,
          email,
          password,
        );

        // Keep credential failures on the login form. The local submit state
        // covers that request; the app-wide loader is only needed after
        // Firebase has accepted the user and account initialization begins.
        setIsLoading(true);
        setBrowserActiveUser(userCredential.user.uid);
        return userCredential;
      } catch (error) {
        // A failed credential attempt does not trigger authenticated-user
        // initialization, so restore the login form here.
        setIsLoading(false);
        throw error;
      } finally {
        loginInProgressRef.current = false;
      }
    },
    [setBrowserActiveUser],
  );

  const register = useCallback(
    async (email: string, password: string): Promise<UserCredential> => {
      loginInProgressRef.current = true;

      try {
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password,
        );

        setBrowserActiveUser(userCredential.user.uid);
        return userCredential;
      } finally {
        loginInProgressRef.current = false;
      }
    },
    [setBrowserActiveUser],
  );

  const hasPermission = useCallback(
    (action: string, ctx?: ResourceContext): boolean => {
      if (!currentUser) return false;

      return doesUserHavePermission(currentPolicies, action, ctx);
    },
    [currentUser, currentPolicies],
  );

  const value = useMemo(
    () => ({
      currentUser,
      currentRoles,
      currentOrganizationRoles,
      currentOrganization,
      currentUserInfo,
      currentOrganizationRolePolicies,
      userAddedInRolePolicy,
      currentPolicies,
      subscriptionDetails,
      hasPermission,
      login,
      register,
      logout,
      isLoading,
      authInitializationError,
      authInitializationFailureStatus,
      retryAuthInitialization,
      completeInvitationAcceptance,
      needsEmailVerification,
      needsMfaEnrollment,
      requiresPasswordChange,
      captureAuthIdentity,
      finalizePasswordChange,
      requiresOnboarding,
      setRequiresOnboarding,
      impersonatedUserId,
      setImpersonation,
    }),
    [
      currentUser,
      currentRoles,
      currentOrganizationRoles,
      currentOrganization,
      currentUserInfo,
      currentOrganizationRolePolicies,
      userAddedInRolePolicy,
      currentPolicies,
      subscriptionDetails,
      hasPermission,
      login,
      register,
      logout,
      isLoading,
      authInitializationError,
      authInitializationFailureStatus,
      retryAuthInitialization,
      completeInvitationAcceptance,
      needsEmailVerification,
      needsMfaEnrollment,
      requiresPasswordChange,
      captureAuthIdentity,
      finalizePasswordChange,
      requiresOnboarding,
      impersonatedUserId,
      setImpersonation,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
